import {
  GridColDef,
  GridColumns,
  GridRenderCellParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import DataGridCell from 'components/common/DataGridCell';
import { filterOperators, numberFilterOperators } from '../../../../constants';
import { Dayjs } from 'dayjs';
import { NavLink } from 'react-router-dom';
import { baseCell } from '../cells/base-cell';
import { currencyFormatter } from 'helpers/currency-formatter';
import productStatusCell from '../cells/product-status-cell';
import { currencyCell } from '../cells/currency-cell';
import { isDirector } from 'components/helpers/IsDirector';
import { isSupplier } from 'components/helpers/IsSupplier';
import { isTechnician } from 'components/helpers/IsTechnician';

export function ConsumptionsColumns(startDate: Dayjs, endDate: Dayjs): GridColDef[] {
  const { t } = useTranslation();
  const translationPrefix = 'entities.movement';
  const productLinkDisable = isDirector() || isSupplier();
  const isTec = isTechnician();
  const isDir = isDirector();
  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];
  const columns: GridColumns = [];
  columns.push(
    {
      field: 'product',
      headerName: t(`global.product`),
      filterOperators,
      width: 150,
      renderCell: (params: GridRenderCellParams<string>) => {
        if (productLinkDisable) {
          return <span className="text-primary-600">{params.value}</span>;
        }
        return (
          <NavLink to={`../products/${params.row.product_id}`}>
            <b className="text-primary-600">{params.value}</b>
          </NavLink>
        );
      }
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 150,
      type: 'singleSelect',
      valueOptions: [
        { value: 'ACTIVE', label: t('entities.product.status_options.ACTIVE') },
        { value: 'DECOMMISSIONED', label: t('entities.product.status_options.DECOMMISSIONED') },
        {
          value: 'TO_BE_DECOMMISSIONED',
          label: t('entities.product.status_options.TO_BE_DECOMMISSIONED')
        },
        { value: 'TENDER', label: t('entities.product.status_options.TENDER') },
        { value: 'BLOCKED', label: t('entities.product.status_options.BLOCKED') }
      ],
      renderCell: productStatusCell(t)
    },
    {
      field: 'product_type',
      valueGetter: (params: GridValueFormatterParams) => params.value,
      renderCell: baseCell,
      headerName: t(`global.product_type`),
      type: 'string',
      width: 100,
      filterOperators
    }
  );
  if (!isTec && !isDir) {
    columns.push({
      field: 'unit_price',
      headerName: t(`${translationPrefix}.unit_price`),
      type: 'number',
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    });
  }

  columns.push(
    {
      field: 'amount',
      type: 'number',
      headerName: t(`${translationPrefix}.amount`),
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      hide: isDir,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'avg',
      type: 'number',
      headerName: t(`${translationPrefix}.avg_quantity`),
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      renderCell: (params: GridRenderCellParams<string>) => (
        <b className="text-primary-600"> {params.value} </b>
      )
    },
    {
      field: 'tot',
      type: 'number',
      headerName: t(`${translationPrefix}.total`),
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      renderCell: (params: GridRenderCellParams<string>) => (
        <b className="text-primary-600"> {params.value} </b>
      )
    },
    {
      field: 'label',
      type: 'string',
      renderCell: baseCell,
      headerName: t(`global.label`),
      width: 250,
      hide: isDir
    },
    {
      field: 'description',
      type: 'string',
      headerName: t(`global.description`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      type: 'string',
      headerName: t(`global.supplier`),
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'notes',
      type: 'string',
      headerName: t(`global.notes`),
      width: 250,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'category',
      type: 'string',
      headerName: t(`global.category`),
      width: 200,
      renderCell: baseCell,
      hide: isDir
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      filterOperators,
      sortable: true,
      width: 250,
      renderCell: baseCell
    }
  );

  if (endDate && startDate) {
    for (
      let currentDate = startDate;
      endDate.isAfter(currentDate);
      currentDate = currentDate.add(1, 'month')
    ) {
      const year = currentDate.year();
      const month = currentDate.month();

      columns.push({
        field: `m_${month}__y_${year}`,
        type: 'number',
        headerName: `${t(`months.${monthNames[month]}`)} ${year}`,
        filterable: false,
        sortable: false,
        width: 100,
        renderCell: (params: GridRenderCellParams<string>) => (
          <DataGridCell content={params.value} />
        )
      });
    }
  }

  return columns;
}
